import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import './styles/variables.css'
import './styles/global.css'
import './styles/components.css'
import i18n from './i18n/config'
import App from './App.tsx'

// Import preloadable route components — each has a .preload() method
// that resolves the module and stores .default, so the Wrapper renders
// the real component synchronously during hydration (zero Suspense fallback).
import {
  LandingPage,
  LoginPage,
  SignupPage,
  VerifyEmailPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  AcceptInvitePage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  CookiePolicyPage,
  FAQPage,
  BlogListPage,
  BlogCategoryPage,
  BlogPostPage,
  FeaturePage,
  WalkthroughPage,
  ReviewGatePage,
  AboutPage,
  SecurityPage,
  NotFoundPage,
} from './App'

const rootElement = document.getElementById('root')!;

import React from 'react';
class ErrorBoundary extends React.Component<any, { hasError: boolean, error: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) { return { hasError: true, error }; }
  componentDidCatch(error: any, errorInfo: any) { console.error('ErrorBoundary caught:', error, errorInfo); }
  render() {
    if (this.state.hasError) return <div style={{ color: 'red' }}><h1>App Crashed</h1><pre>{String(this.state.error?.stack)}</pre></div>;
    return this.props.children;
  }
}

const app = (
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);

// ── Route matcher: returns .preload() promises for the matched route ──
// DemoBanner, PromotionalModal, LanguageRouteWrapper, EnglishRouteWrapper
// are now STATIC imports in App.tsx — they need no preloading.
// Only the page component for the current route needs preloading.
const SUPPORTED_LANGS = ['en', 'es', 'fr', 'de', 'it', 'tr', 'ar', 'bn'];

function getPreloadPromises(): Promise<any>[] {
  const pathname = window.location.pathname;

  // Strip locale prefix: /ar/blog → /blog, /fr/ → /
  let path = pathname;
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGS.includes(segments[0])) {
    path = '/' + segments.slice(1).join('/');
  }
  if (path !== '/' && path.endsWith('/')) {
    path = path.slice(0, -1);
  }

  console.log('Preload path:', path);

  // Match the route to its page component and call .preload()
  if (path === '/' || path === '') return [LandingPage.preload()];
  if (path === '/login') return [LoginPage.preload()];
  if (path === '/signup') return [SignupPage.preload()];
  if (path === '/verify-email') return [VerifyEmailPage.preload()];
  if (path === '/forgot-password') return [ForgotPasswordPage.preload()];
  if (path === '/reset-password') return [ResetPasswordPage.preload()];
  if (path === '/accept-invite') return [AcceptInvitePage.preload()];
  if (path === '/privacy-policy') return [PrivacyPolicyPage.preload()];
  if (path === '/terms-of-service') return [TermsOfServicePage.preload()];
  if (path === '/cookie-policy') return [CookiePolicyPage.preload()];
  if (path === '/faq') return [FAQPage.preload()];
  if (path === '/about') return [AboutPage.preload()];
  if (path === '/security') return [SecurityPage.preload()];
  if (path === '/blog') return [BlogListPage.preload()];
  if (path.startsWith('/blog/category/')) return [BlogCategoryPage.preload()];
  if (path.startsWith('/blog/')) return [BlogPostPage.preload()];
  if (path.startsWith('/features/')) return [FeaturePage.preload()];
  if (path.startsWith('/walkthroughs/')) return [WalkthroughPage.preload()];
  if (path.startsWith('/r/')) return [ReviewGatePage.preload()];

  // Not a prerendered route (dashboard, etc.) — nothing to preload
  return [NotFoundPage.preload()];
}

// ── Bootstrap ──
const renderApp = () => {
  if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, app, {
      onRecoverableError: (error, errorInfo) => {
        console.error('Hydration Error:', error instanceof Error ? error.message : error);
        console.error('Hydration Error Info:', JSON.stringify(errorInfo));
      }
    });
  } else {
    createRoot(rootElement).render(app);
  }
  // Signal to the prerender script that React has committed and the DOM is ready.
  // requestAnimationFrame ensures react-helmet-async has flushed <title>, meta, etc.
  requestAnimationFrame(() => {
    // Defer prerender readiness for blog pages until dynamic content is fetched
    if (!window.location.pathname.includes('/blog')) {
      (window as any).__PRERENDER_READY = true;
    }
  });
};

if (rootElement.hasChildNodes()) {
  // Hydration path: wait for BOTH i18n AND the matched route's preload
  const i18nReady = i18n.isInitialized
    ? Promise.resolve()
    : new Promise<void>(resolve => i18n.on('initialized', resolve));

  const preloads = getPreloadPromises();

  Promise.all([i18nReady, ...preloads]).then(renderApp);
} else {
  // Client-only SPA path (dashboard, etc.) — just wait for i18n
  if (i18n.isInitialized) {
    renderApp();
  } else {
    i18n.on('initialized', renderApp);
  }
}
